/**
 * Admin Dashboard
 *
 * Overview page showing:
 * - Quick stats
 * - Upcoming events summary
 *
 * Uses React Query with polling for real-time updates.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAdminDashboard } from '../../hooks/queries';
import { DataFreshness } from '../../components/ui/DataFreshness';
import { formatDate, formatInTimeZone } from '../../utils/dateFormatting';

export default function AdminDashboard() {
  const { user } = useAuth();
  const {
    stats,
    upcomingEvents,
    loading,
    error,
    isRefetching,
    dataUpdatedAt,
    refetch,
  } = useAdminDashboard();

  return (
    <div>
      {/* Page header — the title rule replaces the old drop shadow as the
          separator between chrome and content. */}
      <div className="mb-8 pb-4 border-b border-hairline flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-semibold tracking-heading text-ink">Dashboard</h1>
          <p className="text-ink-body mt-1">
            Welcome back, {user?.firstName}! Here's what's happening.
          </p>
        </div>
        <DataFreshness
          dataUpdatedAt={dataUpdatedAt}
          onRefresh={refetch}
          isRefreshing={isRefetching}
        />
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6 p-4 bg-white border border-hairline border-l-[3px] border-l-program text-program-dark">
          {error}
        </div>
      )}

      {/* Stats grid — a narrow count beside the wide staffing tile. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Upcoming Events"
          value={String(stats?.upcomingEvents ?? 0)}
          subtitle="Next 7 days"
          loading={loading}
        />
        <MetricPairCard
          title="Staffing, next 7 days"
          className="md:col-span-2"
          loading={loading}
          metrics={[
            {
              value: String(stats?.roleSlots ?? 0),
              label: 'roles',
              note: `${stats?.rolesUnfilled ?? 0} unfilled`,
            },
            {
              value: String(stats?.crewResponded ?? 0),
              label: 'crew have replied',
              note: `of ${stats?.activeCrew ?? 0} active crew`,
            },
          ]}
        />
      </div>

      {/* Resources */}
      <Link
        to="/admin/resources"
        className="mb-8 flex items-center justify-between gap-4 px-6 py-4 bg-white border border-hairline hover:bg-surface-hover transition-colors"
      >
        <div className="min-w-0">
          <p className="text-base font-semibold text-ink">Resources</p>
          <p className="text-sm text-ink-muted">Links, files and notes for your whole organization</p>
        </div>
        <span className="text-sm text-ink-body font-medium whitespace-nowrap">Open resources →</span>
      </Link>

      {/* Upcoming Events */}
      <div className="bg-white border border-hairline">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-hairline">
          <h2 className="text-lg font-semibold text-ink">Upcoming Events</h2>
          <Link
            to="/admin/events"
            className="text-sm text-ink-body hover:text-ink font-medium whitespace-nowrap"
          >
            View all events →
          </Link>
        </div>
        {loading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-100 w-1/2"></div>
              </div>
            ))}
          </div>
        ) : upcomingEvents.length === 0 ? (
          <p className="p-6 text-ink-muted text-sm">No upcoming events</p>
        ) : (
          <div>
            {upcomingEvents.map((event) => (
              <EventItem
                key={event.id}
                id={event.id}
                name={event.name}
                venue={event.venue}
                date={formatDate(event.eventDate)}
                callTime={event.callTime}
                startTime={event.startTime}
                status={event.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                crew={event.crew}
                activeCrew={stats?.activeCrew ?? 0}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  loading?: boolean;
}

/**
 * A stat tile is a number, not a decorated icon. The 3px graphite rail on top
 * is the only ornament, and colour is reserved for status meaning elsewhere on
 * the page — a count is never good or bad on its own.
 */
function StatCard({ title, value, subtitle, loading }: StatCardProps) {
  return (
    <div className="bg-white border border-hairline border-t-[3px] border-t-rack p-6">
      <h3 className="text-sm font-medium text-ink-body">{title}</h3>
      {loading ? (
        <div className="mt-2 h-[42px] flex items-center">
          <div className="w-6 h-6 border-2 border-rack border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <p className="mt-2 text-[42px] leading-none font-semibold text-ink">{value}</p>
      )}
      <p className="text-[13px] text-ink-muted mt-2">{subtitle}</p>
    </div>
  );
}

interface MetricPairCardProps {
  title: string;
  metrics: { value: string; label: string; note: string }[];
  className?: string;
  loading?: boolean;
}

/**
 * Two related numbers in one tile, sharing StatCard's chrome. Answers the
 * landing question in one glance: how much work is coming, and how many people
 * have said anything about it.
 *
 * Neither number is coloured, "unfilled" included. Same rule as the gray DRAFT
 * row: a count is not good or bad on its own, and colouring the routine case
 * red is how admins learn to ignore the colour that matters.
 */
function MetricPairCard({ title, metrics, className = '', loading }: MetricPairCardProps) {
  return (
    <div
      className={`bg-white border border-hairline border-t-[3px] border-t-rack p-6 ${className}`}
    >
      <h3 className="text-sm font-medium text-ink-body">{title}</h3>
      {loading ? (
        <div className="mt-2 h-[42px] flex items-center">
          <div className="w-6 h-6 border-2 border-rack border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {metrics.map((m) => (
            <div key={m.label}>
              <p className="text-[26px] leading-none font-semibold text-ink">
                {m.value}{' '}
                <span className="text-sm font-medium text-ink-body">{m.label}</span>
              </p>
              <p className="text-[13px] text-ink-muted mt-1.5">{m.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface EventItemProps {
  id: string;
  name: string;
  venue: string;
  date: string;
  callTime?: string | null;
  startTime?: string | null;
  status: 'Draft' | 'Published';
  crew: {
    assigned: number;
    confirmed: number;
    pending: number;
    needed: number;
    responded: number | null;
  };
  activeCrew: number;
}

function EventItem({
  id,
  name,
  venue,
  date,
  callTime,
  startTime,
  status,
  crew,
  activeCrew,
}: EventItemProps) {
  const allConfirmed = crew.assigned > 0 && crew.confirmed === crew.assigned;
  const hasPending = crew.pending > 0;
  const needsMore = crew.assigned < crew.needed;

  const formatTime = (timeString: string | null | undefined) => {
    if (!timeString) return null;
    return formatInTimeZone(timeString, {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formattedCallTime = formatTime(callTime);
  const formattedStartTime = formatTime(startTime);

  // The left rail is the row's status at a glance. A draft is gray whatever its
  // staffing looks like — nothing about it is actionable yet, so flagging it
  // red would train admins to ignore the colour that matters.
  const rail =
    status === 'Draft'
      ? 'border-l-hairline'
      : needsMore
        ? 'border-l-program'
        : allConfirmed
          ? 'border-l-preview'
          : 'border-l-hairline';

  return (
    <Link
      to={`/admin/events/${id}`}
      className={`block px-6 py-4 border-l-[3px] ${rail} border-b border-b-hairline-light last:border-b-0 hover:bg-surface-hover transition-colors`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-base font-semibold text-ink">{name}</p>
          <p className="text-sm text-ink-muted">
            {venue} • {date}
          </p>
          {(formattedCallTime || formattedStartTime) && (
            <p className="text-sm font-medium text-ink mt-0.5">
              {formattedCallTime && `Call: ${formattedCallTime}`}
              {formattedCallTime && formattedStartTime && ' · '}
              {formattedStartTime && `Start: ${formattedStartTime}`}
            </p>
          )}
        </div>
        <span
          className={`px-2 py-0.5 border text-xs font-medium whitespace-nowrap ${
            status === 'Published'
              ? 'border-preview bg-white text-preview-text'
              : 'border-hairline bg-white text-ink-body'
          }`}
        >
          {status}
        </span>
      </div>
      {/* Crew status row */}
      <div className="mt-2 flex items-center gap-3 text-[13px]">
        <span className={needsMore ? 'text-program-dark font-medium' : 'text-ink-muted'}>
          {crew.assigned}/{crew.needed} assigned
        </span>
        {crew.assigned > 0 && (
          <>
            <span className="text-hairline">•</span>
            {allConfirmed ? (
              <span className="text-preview-text flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                All confirmed
              </span>
            ) : (
              <span className="text-ink-muted">
                {crew.confirmed} confirmed{hasPending && `, ${crew.pending} awaiting`}
              </span>
            )}
          </>
        )}
        <span className="text-hairline">•</span>
        {crew.responded === null ? (
          <span className="text-ink-muted" title="Crew cannot see a draft, so there is nothing for them to answer yet">
            Responses n/a
          </span>
        ) : (
          <span className="text-ink-muted">
            {crew.responded} of {activeCrew} responded
          </span>
        )}
      </div>
    </Link>
  );
}
